import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TopItem } from '../../statistics-data.service';

@Component({
  standalone: true,
  selector: 'app-top-list',
  templateUrl: './top-list.component.html',
  styleUrls: ['./top-list.component.scss'],
  imports: [CommonModule, RouterModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopListComponent {
  @Input() title: string = '';
  @Input() icon: string = 'pi-star';
  @Input() items: TopItem[] = [];
  @Input() loading: boolean = false;
  @Input() valueFormat: 'currency' | 'number' = 'currency';
  @Input() currencyCode: string = 'EUR';
  @Input() showCount: boolean = true;
  @Input() showRank: boolean = true;
  @Input() showTrend: boolean = false;
  @Input() maxItems: number = 5;
  @Input() emptyMessage: string = 'STATISTICS.GENERAL.NO_DATA_AVAILABLE';

  @Output() itemClick = new EventEmitter<TopItem>();
  @Output() seeAllClick = new EventEmitter<void>();

  constructor(private translate: TranslateService) {}

  get displayItems(): TopItem[] {
    return this.items.slice(0, this.maxItems);
  }

  get hasMoreItems(): boolean {
    return this.items.length > this.maxItems;
  }

  formatValue(value: number): string {
    if (this.valueFormat === 'currency') {
      return new Intl.NumberFormat(this.getLocale(), {
        style: 'currency',
        currency: this.currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    }
    return new Intl.NumberFormat(this.getLocale()).format(value);
  }

  getTrendClass(trend: number | undefined): string {
    if (!trend || Math.abs(trend) < 0.01) return 'neutral';
    return trend > 0 ? 'positive' : 'negative';
  }

  getTrendIcon(trend: number | undefined): string {
    if (!trend || Math.abs(trend) < 0.01) return 'pi-minus';
    return trend > 0 ? 'pi-arrow-up' : 'pi-arrow-down';
  }

  onItemClick(item: TopItem): void {
    this.itemClick.emit(item);
  }

  onSeeAll(): void {
    this.seeAllClick.emit();
  }

  // Loading skeleton items
  get loadingItems(): number[] {
    return Array(this.maxItems).fill(0);
  }

  private getLocale(): string {
    const lang = (this.translate.currentLang || 'es').toLowerCase();
    if (lang.startsWith('en')) return 'en-US';
    if (lang.startsWith('ca')) return 'ca-ES';
    return 'es-ES';
  }
}
